import { useState, useEffect, useCallback, useMemo } from 'react';
import ServiceCRUD from 'services/ServiceCRUDFLEXUC';

const systemConfigService = new ServiceCRUD('/api/system-configs');

/** @typedef {'TELEPHONY' | 'MONITORING_QUEUE_CHAT'} TargetOptions */

/**
 * @typedef {Object} SystemConfig
 * @property {number} id
 * @property {string | null} logo_directory
 * @property {number} level_of_service quantos segundos o nível se serviço está configurado
 * @property {TargetOptions} target para qual módulo/parte do sistema este nível de serviço é dedicado
 */

/**
 * Hook para tratar os níveis de serviço do sistema
 * @param {Object} [props={}]
 * @param {string[]} [props.targets=[]] quais tipos de target devem ser retornados
 * @returns {Object} Estado de monitoramento e funções
 */
const useServiceLevels = (props = {}) => {
  const { targets } = props;

  const [state, setState] = useState({
    response: [],
    isLoading: false,
    isError: false,
    error: null
  });

  // Níveis de serviço agrupados por tipo de target
  const groupedByType = useMemo(() => {
    if (!state?.response) return [];

    return state.response.reduce((prev, curr) => {
      // Inicializa a propriedade para este target se ainda não existir
      if (!prev[curr.target]) {
        prev[curr.target] = [];
      }

      prev[curr.target].push(curr);
      return prev;
    }, {});
  }, [state.response]);

  const fetch = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));

    try {
      const response = await systemConfigService.getAllSimple();

      let data = response?.data?.data ?? [];

      // Filtra os tipos de target que devem ser retornados
      if (targets) data = data.filter((sl) => targets.includes(sl.target));

      if (props)
        setState((prevState) => ({
          ...prevState,
          response: data,
          isLoading: false,
          isError: false,
          error: null
        }));
    } catch (err) {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        isError: true,
        error: err
      }));
    }
  }, [JSON.stringify(props.targets)]);

  /**
   * Atualiza um nível de serviço da configuração do sistema
   * @param {number} id - ID da configuração do sistema a ser atualizada
   * @param {Object} data - Novos dados de configuração
   * @returns {Promise<Object>} Dados da configuração atualizada
   */
  const update = useCallback(
    async (data) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));

      try {
        const response = await systemConfigService.updateConfig(data);

        await fetch();

        return response?.data;
      } catch (err) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: true,
          error: err
        }));
        throw err;
      }
    },
    [fetch]
  );

  /**
   * Cria um novo nível de serviço na configuração do sistema
   * @param {Object} data - Dados do nível de serviço (incluindo levelOfService, target, etc)
   * @returns {Promise<Object>} Dados da configuração criada
   */
  const create = useCallback(
    async (data) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        isError: false,
        error: null
      }));

      try {
        const response = await systemConfigService.post(data);

        // Atualiza a lista após criar
        await fetch();

        return response?.data;
      } catch (err) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          isError: true,
          error: err
        }));
        throw err;
      }
    },
    [fetch]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    ...state,
    groupedByType,
    fetch,
    update,
    create
  };
};

export { useServiceLevels };
